<script>
    import Spinner from '../../Spinner.svelte';
    import {sgettext} from '../../../i18n.js';
    import {buildElementIdFromPageNumber} from '../../../utils.js'

    export let current = 1;
    export let total = 0;
    export let pageSize = 50;
    export let next = () => {
    };
    export let previous = () => {
    };
    export let goTo = () => {
    };
    export let loading = false;

    const minimumPageOptions = 3;
    const boundaryPagesCount = 2;
    let totalPages = 0;
    let limit = 1; // limit to 2 pages surrounding current page, ona at each side
    let pagingOptions = [];

    $: totalPages = Math.ceil(total / pageSize);
    $: pagingOptions = getPagingOptions(total, pageSize, current, limit);
    $: totalItemDisplay = getTotalItemCount(total, current);

    function getTotalItemCount(total, current) {
        let text = sgettext('$$ITEMS_DISPLAY$$');
        let upTo = current * pageSize;
        let upToNumber = total < pageSize ? total : upTo > total ? total : upTo;

        if (total) {
            text = `${(current - 1) * pageSize + 1} - ${upToNumber} ${sgettext('$$OF$$')} ${total} ${total > 1 ? sgettext('$$ITEMS_1$$') : sgettext('$$ITEM$$')}`;
        }

        return text;
    }

    function onPrevious(event){
        event.preventDefault();
        previous();
    }

    function onNext(event) {
        event.preventDefault();
        next(totalPages);
    }

    function onGoTo(event, index) {
        event.preventDefault();
        goTo(index);
    }

    function getPagingOptions(total, pgSize, currentPage, limit) {
        const limitThreshold = getLimitThreshold(limit);
        const limited = limit && totalPages > limitThreshold;

        return limited ? getLimitedOptions(totalPages, limit, currentPage) : getAllOptions(totalPages);
    }

    function getAllOptions(totalPages) {
        return new Array(totalPages)
                .fill(null)
                .map((value, index) => ({
                    label: index + 1,
                    value: index + 1,
                    id: buildElementIdFromPageNumber(index + 1)
                }));
    }

    function getLimitedOptions(totalPages, limit, currentPage) {
        const boundarySize = limit * 2 + 2;
        const firstBoundary = 1 + boundarySize;
        const lastBoundary = totalPages - boundarySize;
        const totalShownPages = firstBoundary + 2;

        if (currentPage <= firstBoundary - limit) {
            return Array(totalShownPages)
                    .fill(null)
                    .map((value, index) => {
                        if (index === totalShownPages - 1) {
                            return {
                                label: totalPages,
                                value: totalPages,
                                id: buildElementIdFromPageNumber(totalPages)
                            };
                        } else if (index === totalShownPages - 2) {
                            return {
                                label: '...',
                                value: firstBoundary + 1,
                                id: buildElementIdFromPageNumber(firstBoundary + 1)
                            };
                        }
                        return {
                            label: index + 1,
                            value: index + 1,
                            id: buildElementIdFromPageNumber(index + 1)
                        };
                    });
        } else if (currentPage >= lastBoundary + limit) {
            return Array(totalShownPages)
                    .fill(null)
                    .map((value, index) => {
                        if (index === 0) {
                            return {
                                label: 1,
                                value: 1,
                                id: buildElementIdFromPageNumber(1)
                            };
                        } else if (index === 1) {
                            return {
                                label: '...',
                                value: lastBoundary - 1,
                                id: buildElementIdFromPageNumber(lastBoundary - 1)
                            };
                        }
                        return {
                            label: lastBoundary + index - 2,
                            value: lastBoundary + index - 2,
                            id: buildElementIdFromPageNumber(lastBoundary + index - 2)
                        };
                    });
        } else if (currentPage >= (firstBoundary - limit) && currentPage <= (lastBoundary + limit)) {
            return Array(totalShownPages)
                    .fill(null)
                    .map((value, index) => {
                        if (index === 0) {
                            return {
                                label: 1,
                                value: 1,
                                id: buildElementIdFromPageNumber(1)
                            };
                        } else if (index === 1) {
                            return {
                                label: '...',
                                value: currentPage - limit + (index - 2),
                                id: buildElementIdFromPageNumber(currentPage - limit + (index - 2))
                            };
                        } else if (index === totalShownPages - 1) {
                            return {
                                label: totalPages,
                                value: totalPages,
                                id: buildElementIdFromPageNumber(totalPages)
                            };
                        } else if (index === totalShownPages - 2) {
                            return {
                                label: '...',
                                value: currentPage + limit + 1,
                                id: buildElementIdFromPageNumber(currentPage + limit + 1)
                            };
                        }
                        return {
                            label: currentPage - limit + (index - 2),
                            value: currentPage - limit + (index - 2), 
                            id: buildElementIdFromPageNumber(currentPage - limit + (index - 2))
                        };
                    });
        }

    }

    function getLimitThreshold(limit) {
        return limit * 2 + minimumPageOptions + boundaryPagesCount;
    }
</script>

<style>
    .wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .pagination {
        margin: 0;
    }

    .icon-loading {
        display: flex;
        height: 38px;
        background-color: transparent;
        align-items: center;
    }
</style>

<div class="wrapper">
    <nav aria-label="...">
        <ul class="pagination">
            <li class:disabled={current === 1} class="page-item" on:click={onPrevious}>
                <a class="page-link" href="#" tabindex="-1" aria-disabled="true"><i class="fas fa-chevron-left"></i></a>
            </li>
            {#each pagingOptions as option}
                <li
                        class:active={option.value === current}
                        class="page-item">
                    <a class="page-link" id={option.id} href="" on:click={(event) => onGoTo(event, option.value)}>{option.label}
                        {#if option.value === current}
                            <span class="sr-only">(current)</span>
                        {/if}
                    </a>
                </li>
            {/each}
            <li class:disabled={current === totalPages} class="page-item">
                <a
                        class="page-link"
                        href=""
                        on:click={onNext}
                >
                    <i class="fas fa-chevron-right"></i>
                </a>
            </li>
            {#if loading}
                <li class="page-item"><a class="page-link icon-loading">
                    <Spinner size="small" inline="{true}"/>
                </a></li>
            {/if}
        </ul>


    </nav>
    <div class="item-count">{totalItemDisplay}</div>
</div>
